import { Loader } from "@/shared/ui/loader"
import { Ticket } from "../ticket"
import LensSvg from "@/shared/assets/images/MagnifyingGlass.svg"
import {
    getSearchFlightTotalPrice,
    selectFlightsFirstLoad,
    selectFlights,
    selectFlightsLoading,
} from "@/entities/flight"
import { useAppSelector } from "@/shared/lib/redux"
import { ISearchFlight } from "@/shared/api/aviabooking/types"
import { useEffect, useMemo, useState } from "react"
import Select, {
    type ClassNamesConfig,
    type GroupBase,
    type OptionProps,
    components as rsComponents,
} from "react-select"
import ArrowDown from "@/shared/assets/images/ArrowDown.svg"
import type { useSavedFlights } from "../../lib/use-saved-flights"
import { filterFlightsByPrice } from "../../lib/filter-flights-by-price"

type SortKey = "price" | "departure" | "duration"
type SortDir = "asc" | "desc"

type SortOption<T extends string> = { value: T; label: string }

const SORT_KEY_OPTIONS: SortOption<SortKey>[] = [
    { value: "price", label: "По цене" },
    { value: "departure", label: "По времени вылета" },
    { value: "duration", label: "По длительности" },
]

const SORT_DIR_OPTIONS: SortOption<SortDir>[] = [
    { value: "asc", label: "По возрастанию" },
    { value: "desc", label: "По убыванию" },
]

function sortSelectClassNames<T extends string>(): ClassNamesConfig<
    SortOption<T>,
    false,
    GroupBase<SortOption<T>>
> {
    return {
        control: ({ isFocused }) =>
            [
                "!flex !items-center !min-h-[38px] !rounded-2xl !border !border-slate-200 !shadow-none text-sm cursor-pointer bg-white",
                isFocused ? "!ring-2 ring-color-primary/30 !border-color-primary/25" : "",
            ].join(" "),
        valueContainer: () => "!flex-1 !min-w-0 !py-0 !px-1",
        singleValue: () => "!text-slate-900 !font-medium",
        input: () => "!m-0 !p-0",
        indicatorsContainer: () => "!text-slate-400",
        dropdownIndicator: () => "!p-2",
        indicatorSeparator: () => "!hidden",
        menu: () =>
            "!rounded-2xl !shadow-lg !border !border-slate-200/90 !bg-white !text-sm !overflow-hidden !z-[20] !mt-0 !py-1 !px-1",
        menuList: () => "!py-0 !max-h-none",
        option: ({ isDisabled, isFocused, isSelected }) =>
            [
                "!cursor-pointer !rounded-xl !mx-0 !my-0.5 !px-2 !py-0 !text-left !text-sm transition-colors duration-150",
                isDisabled ? "!cursor-not-allowed !opacity-45" : "",
                !isDisabled && isSelected ? "!bg-color-primary/10" : "",
                !isDisabled && isFocused && !isSelected ? "!bg-slate-100" : "",
            ].join(" "),
    }
}

function SortSelectOption<T extends string>(
    optProps: OptionProps<SortOption<T>, false, GroupBase<SortOption<T>>>,
) {
    const { isSelected, isFocused, isDisabled, data } = optProps
    const rowTextClass = isDisabled
        ? "text-slate-500"
        : isSelected && !isDisabled
          ? "text-color-primary"
          : isFocused
            ? "text-slate-900"
            : "text-slate-800"
    return (
        <rsComponents.Option {...optProps}>
            <div
                className={`flex w-full items-center justify-between gap-3 py-2.5 pl-3 pr-2.5 min-h-[42px] ${rowTextClass}`}
            >
                <span
                    className={[
                        "leading-snug",
                        isSelected && !isDisabled ? "font-semibold" : "font-normal",
                    ].join(" ")}
                >
                    {data.label}
                </span>
            </div>
        </rsComponents.Option>
    )
}

function parsePriceInput(value: string): number | null {
    const trimmed = value.trim()
    if (!trimmed) return null
    const n = parseFloat(trimmed.replace(",", "."))
    return Number.isFinite(n) ? n : null
}

function getDepartureTimestamp(flight: ISearchFlight): number {
    const raw = flight.segments[0]?.dep_date_time
    if (!raw) return 0
    const ms = new Date(raw).getTime()
    return Number.isFinite(ms) ? ms : 0
}

function TicketListSortSelect<T extends string>(props: {
    inputId: string
    label: string
    options: SortOption<T>[]
    value: T
    onChange: (value: T) => void
    minWidthClass: string
}) {
    const { inputId, label, options, value, onChange, minWidthClass } = props
    const selected = options.find((o) => o.value === value) ?? null
    return (
        <div className={`flex flex-col gap-1 ${minWidthClass}`}>
            <label htmlFor={inputId} className="text-sm text-slate-500">
                {label}
            </label>
            <Select<SortOption<T>, false, GroupBase<SortOption<T>>>
                inputId={inputId}
                instanceId={inputId}
                unstyled
                isSearchable={false}
                options={options}
                value={selected}
                onChange={(opt) => {
                    if (opt) onChange(opt.value)
                }}
                components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: (indicatorProps) => (
                        <rsComponents.DropdownIndicator {...indicatorProps}>
                            <ArrowDown width={14} height={14} className="shrink-0" />
                        </rsComponents.DropdownIndicator>
                    ),
                    Option: (optionProps) => <SortSelectOption<T> {...optionProps} />,
                }}
                classNames={sortSelectClassNames<T>()}
            />
        </div>
    )
}

export const TicketList = ({ saved }: { saved: ReturnType<typeof useSavedFlights> }) => {
    const flightsFirstLoad = useAppSelector(selectFlightsFirstLoad)
    const flights = useAppSelector(selectFlights)
    const flightsLoading = useAppSelector(selectFlightsLoading)
    const offerIds = useMemo(() => flights.map(flight => flight.id).join("\n"), [flights])
    useEffect(() => { void saved.lookup(flights.map(flight => flight.id)) }, [offerIds, saved.lookup])
    const [minPriceStr, setMinPriceStr] = useState("")
    const [maxPriceStr, setMaxPriceStr] = useState("")
    const [sortKey, setSortKey] = useState<SortKey>("price")
    const [sortDir, setSortDir] = useState<SortDir>("asc")

    const minPrice = parsePriceInput(minPriceStr)
    const maxPrice = parsePriceInput(maxPriceStr)

    const filteredFlights = useMemo(() => {
        if (!flights.length) return []
        const filtered = filterFlightsByPrice(flights, minPrice, maxPrice)
        const sign = sortDir === "asc" ? 1 : -1
        return [...filtered].sort((a, b) => {
            let va: number
            let vb: number
            switch (sortKey) {
                case "price":
                    va = getSearchFlightTotalPrice(a)
                    vb = getSearchFlightTotalPrice(b)
                    break
                case "departure":
                    va = getDepartureTimestamp(a)
                    vb = getDepartureTimestamp(b)
                    break
                case "duration":
                    va = a.total_duration
                    vb = b.total_duration
                    break
            }
            if (va === vb) return 0
            return va < vb ? -sign : sign
        })
    }, [flights, minPrice, maxPrice, sortKey, sortDir])

    const filterActive = minPrice !== null || maxPrice !== null
    const hasRawResults = Boolean(flights.length)
    const hasFilteredResults = Boolean(filteredFlights.length)

    return (
        <>
            {saved.lookupError && <p role="alert">{saved.lookupError} <button type="button" className="underline"
                onClick={() => void saved.lookup(flights.map(flight => flight.id))}>Повторить</button></p>}
            {hasRawResults && (
                <div className="bg-white flex rounded-3xl shadow-sm p-4 mb-5 flex-wrap items-end gap-4">
                    <TicketListSortSelect
                        inputId="ticket-sort-key"
                        label="Сортировка"
                        options={SORT_KEY_OPTIONS}
                        value={sortKey}
                        onChange={(v) => setSortKey(v)}
                        minWidthClass="min-w-[200px]"
                    />
                    <TicketListSortSelect
                        inputId="ticket-sort-dir"
                        label="Порядок"
                        options={SORT_DIR_OPTIONS}
                        value={sortDir}
                        onChange={(v) => setSortDir(v)}
                        minWidthClass="min-w-[180px]"
                    />
                    <div className="flex flex-col gap-1 min-w-[140px]">
                        <label htmlFor="ticket-price-min" className="text-sm text-slate-500">
                            Цена от
                        </label>
                        <input
                            id="ticket-price-min"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="Без ограничения"
                            value={minPriceStr}
                            onChange={(e) => setMinPriceStr(e.target.value)}
                            className="border rounded-2xl py-2 px-3 text-sm w-full min-w-[140px] focus:outline-none focus:ring-2 focus:ring-color-primary/30"
                        />
                    </div>
                    <div className="flex flex-col gap-1 min-w-[140px]">
                        <label htmlFor="ticket-price-max" className="text-sm text-slate-500">
                            Цена до
                        </label>
                        <input
                            id="ticket-price-max"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="Без ограничения"
                            value={maxPriceStr}
                            onChange={(e) => setMaxPriceStr(e.target.value)}
                            className="border rounded-2xl py-2 px-3 text-sm w-full min-w-[140px] focus:outline-none focus:ring-2 focus:ring-color-primary/30"
                        />
                    </div>
                    {filterActive && (
                        <button
                            type="button"
                            onClick={() => {
                                setMinPriceStr("")
                                setMaxPriceStr("")
                            }}
                            className="text-sm text-color-primary hover:underline py-2"
                        >
                            Сбросить
                        </button>
                    )}
                </div>
            )}
            {hasFilteredResults && (
                <div className="flex flex-col gap-5">
                    {filteredFlights.map((flight) => (
                        <Ticket flight={flight} key={flight.id} saved={saved} />
                    ))}
                </div>
            )}
            {hasRawResults && !hasFilteredResults && !flightsLoading && (
                <div className="w-full flex text-lg text-center justify-center items-center gap-1 text-slate-400">
                    <LensSvg height={20} width={20} /> Нет рейсов в выбранном диапазоне цен
                </div>
            )}
            {(!hasRawResults && flightsLoading) && (
                <div className="mt-4">
                    <Loader />
                </div>
            )}
            {(!hasRawResults && !flightsLoading && flightsFirstLoad) && (
                <div className="w-full flex text-lg text-center justify-center items-center gap-1 text-slate-400">
                    <LensSvg height={20} width={20} /> Попробуйте найти авиабилеты
                </div>
            )}
            {(!hasRawResults && !flightsLoading && !flightsFirstLoad) && (
                <div className="w-full flex text-lg text-center justify-center items-center gap-1 text-slate-400">
                    <LensSvg height={20} width={20} /> Не удалось найти авиабилеты
                </div>
            )}
        </>
    )
}
